// screens/DisputeScreen.js
// BuzzInApp — Pantalla de disputa / reporte de problemas
//
// Props:
//   redemption   { redemptionId, businessId, offer }
//   business     object del comercio
//   authHook     retorno de useAuth
//   onBack       () => void

import React, { useState } from "react";
import { addDocument, COLLECTIONS } from "../config/firebase";
import useTranslation from "../i18n/useTranslation";
import "./DisputeScreen.css";

const IconBack = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DISPUTE_TYPES = [
  "prize_not_given",
  "qr_not_working",
  "wrong_prize",
  "location_error",
  "other",
];

const DisputeScreen = ({ redemption, business, authHook, onBack }) => {
  const { t } = useTranslation();
  const { user } = authHook;

  const [type, setType]               = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading]         = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [error, setError]             = useState(null);

  const canSubmit = type && description.trim().length >= 10 && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);

    try {
      await addDocument(COLLECTIONS.DISPUTES, {
        uid:          user.uid,
        redemptionId: redemption?.redemptionId ?? null,
        businessId:   redemption?.businessId ?? business?.id,
        type,
        description:  description.trim(),
        status:       "open",
        // La evidencia completa (GPS, IP, clicks) ya está en el redemption doc
      });
      setSubmitted(true);
    } catch (err) {
      console.error("[DisputeScreen] submit error:", err);
      setError(t("dispute.submitError"));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="dispute-screen dispute-screen--submitted">
        <div className="dispute-screen__success-icon" aria-hidden="true">✓</div>
        <h2 className="dispute-screen__success-title">{t("dispute.submitSuccess")}</h2>
        <p className="dispute-screen__sla">{t("dispute.slaNote")}</p>
        <button className="dispute-screen__btn dispute-screen__btn--primary" onClick={onBack}>
          {t("app.back")}
        </button>
      </div>
    );
  }

  return (
    <div className="dispute-screen">
      {/* Header */}
      <header className="dispute-screen__header">
        <button className="dispute-screen__back" onClick={onBack} aria-label={t("app.back")}>
          <IconBack />
        </button>
        <h1 className="dispute-screen__title">{t("dispute.title")}</h1>
        <div style={{ width: 40 }} />
      </header>

      <div className="dispute-screen__body">
        <p className="dispute-screen__subtitle">{t("dispute.subtitle")}</p>

        {/* Tipo de problema */}
        <div className="dispute-screen__field">
          <label className="dispute-screen__label">{t("dispute.typeLabel")}</label>
          <div className="dispute-screen__type-list" role="radiogroup">
            {DISPUTE_TYPES.map((key) => (
              <button
                key={key}
                className={`dispute-screen__type-btn ${type === key ? "dispute-screen__type-btn--active" : ""}`}
                onClick={() => setType(key)}
                role="radio"
                aria-checked={type === key}
              >
                {t(`dispute.types.${key}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Descripción */}
        <div className="dispute-screen__field">
          <label className="dispute-screen__label" htmlFor="dispute-desc">
            {t("dispute.descriptionLabel")}
          </label>
          <textarea
            id="dispute-desc"
            className="dispute-screen__textarea"
            placeholder={t("dispute.descriptionPlaceholder")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
          />
          <span className="dispute-screen__char-count">
            {description.length} / 500
          </span>
        </div>

        {error && (
          <p className="dispute-screen__error" role="alert">{error}</p>
        )}

        <p className="dispute-screen__sla">{t("dispute.slaNote")}</p>

        <button
          className="dispute-screen__btn dispute-screen__btn--primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-busy={loading}
        >
          {loading
            ? <span className="dispute-screen__spinner" aria-hidden="true" />
            : null
          }
          {t("dispute.submit")}
        </button>
      </div>
    </div>
  );
};

export default DisputeScreen;
